import React from 'react';
import Header from './Header';
import background from './img/softwarePartners.png';
import MobileNavigation from './MobileNavigation';
import { CopyBlock, dracula } from 'react-code-blocks';
import Footer from './Footer';
import { Link } from 'react-router-dom';
const DevCustomerVault = () => {
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121]">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">Customer Vault API Guide</h3>
          </div>
          <div className="flex p-8 md:p-16 row flex-col justify-center gap-6 rounded-[28px] bg-white !mb-10 text-black [&_p]:text-black">
            <p className="light" style={{ textWrap: 'wrap', textWrapMode: 'auto' }}>
              This document outlines the Customer Vault APIs and the recommended flow for creating,
              storing, and managing customers and their associated payment methods.
            </p>

            {/* 1. CREATE CUSTOMER */}
            <h2 className="text-black large">1. Create a Customer</h2>

            <p className="light">
              To vault a customer and their payment information, use the{' '}
              <a
                href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/POST/api/gateway/{gatewayId}/customer"
                target="_blank"
                rel="noreferrer"
                className="links"
              >
                Create Customer API
              </a>
              . This endpoint allows you to:
            </p>

            <ul className="ml-6 list-disc">
              <li className="text-black light">Create a customer profile</li>
              <li className="text-black light">Store one or more payment methods (card or ACH)</li>
              <li className="text-black light">Associate addresses and custom fields</li>
            </ul>

            <h3 className="mt-4 text-2xl font-medium text-black">
              1.1 Vaulting Payment Information
            </h3>

            <p className="light" style={{ textWrap: 'wrap', textWrapMode: 'auto' }}>
              When storing payment data, you must use a <b>token generated by the tokenizer</b>. Raw
              payment data should never be passed directly. For a guide on tokenizing card and ACH data, see the{' '} <Link to="/developer/tokenizer" className="links">Tokenizer Guide</Link>.
            </p>

            {/* CARD */}
            <h4 className="mt-4 font-medium text-black">Card Requirements</h4>

            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <code>token</code> — Token returned from the iFrame tokenizer
              </li>
              <li className="text-black light">
                <code>maskedCard</code> — First 6 and last 4 digits
              </li>
              <li className="text-black light">
                <code>expirationDate</code>
              </li>
              <li className="text-black light">
                <code>cardType</code> (e.g., Visa, Mastercard)
              </li>
            </ul>

            {/* ACH */}
            <h4 className="mt-4 font-medium text-black">ACH Requirements</h4>

            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <code>token</code> — Token returned from ACH tokenization
              </li>
              <li className="text-black light">
                <code>maskedAccount</code>
              </li>
              <li className="text-black light">
                <code>accountType</code> — Checking or Savings
              </li>
              <li className="text-black light">
                <code>routingNumber</code>
              </li>
              <li className="text-black light">
                <code>secCode</code> — Recommended value: <code>"WEB"</code> for API integrations
              </li>
            </ul>

            <div className="callout">
              <p className="mb-0 light">
                <strong>Note:</strong> While the customer name is required, providing additional
                fields (address, email, etc.) is strongly recommended to improve transaction success
                rates.
              </p>
            </div>

            {/* REQUEST */}
            <h3 className="mt-6 text-2xl font-medium text-black">1.2 Request</h3>

            <p className="light">
              <strong>Endpoint</strong>
            </p>

            <CopyBlock
              text={`POST /api/gateway/{gatewayId}/customer`}
              language="bash"
              theme={dracula}
            />

            <p className="mt-4 light">
              <strong>Example Request Body</strong>
            </p>

            <CopyBlock
              text={`{
  "name": "",
  "description": null,
  "referenceId": null,
  "addresses": [
    {
      "addressLine1": null,
      "addressLine2": null,
      "city": null,
      "state": null,
      "postalCode": null,
      "country": null,
      "firstName": null,
      "lastName": null,
      "email": null,
      "phone": null,
      "company": null,
      "isShipping": true,
      "isBilling": true,
      "isPhysical": true
    }
  ],
  "customFields": [
    {
      "customFieldId": 1,
      "customFieldValue": [""]
    }
  ],
  "payment": {
    "card": {
      "maskedCard": null,
      "token": null,
      "cardType": "Visa",
      "expirationDate": null
    },
    "ach": {
      "maskedAccount": null,
      "accountType": "Checking",
      "secCode": "WEB",
      "token": null,
      "routingNumber": null
    },
    "isDefault": true
  }
}`}
              language="json"
              theme={dracula}
            />

            <h3 className="mt-6 text-2xl font-medium text-black">1.3 Response</h3>

            <p className="light">
              A successful request returns a <strong>customerId</strong>, which represents the
              vaulted customer and is required for all future interactions.
            </p>

            <CopyBlock
              text={`{
  "statusCode": "Continue",
  "statusDetails": ["…"],
  "data": {
    "customerId": "123e4567-e89b-12d3-a456-426614174000"
  }
}`}
              language="json"
              theme={dracula}
            />

            {/* 2. RETRIEVE */}
            <h2 className="text-black large">2. Retrieve a Customer (Get by ID)</h2>

            <p className="light" style={{ textWrap: 'wrap', textWrapMode: 'auto' }}>
              To perform transactions (one-time or recurring) using stored payment methods, retrieve
              the customer using the{' '}
              <a
                href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/GET/api/gateway/{gatewayId}/customer/{customerId}"
                target="_blank"
                rel="noreferrer"
                className="links"
              >
                Get Customer by ID API
              </a>
              .
            </p>

            <p className="light">This response includes:</p>
            <ul className="ml-6 list-disc">
              <li className="text-black light">Stored payment methods (cardId, achId)</li>
              <li className="text-black light">Address IDs</li>
              <li className="text-black light">Subscription data</li>
              <li className="text-black light">Customer metadata</li>
            </ul>

            <h3 className="mt-6 text-2xl font-medium text-black">2.1 Request</h3>

            <CopyBlock
              text={`GET /api/gateway/{gatewayId}/customer/{customerId}`}
              language="bash"
              theme={dracula}
            />

            <h3 className="mt-6 text-2xl font-medium text-black">2.2 Response Overview</h3>

            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <strong>paymentMethods</strong> — contains <code>cardId</code> and/or{' '}
                <code>achId</code>, indicates default payment method
              </li>
              <li className="text-black light">
                <strong>addresses</strong> — includes <code>customerAddressId</code>
              </li>
              <li className="text-black light">
                <strong>subscriptions</strong> — active or historical recurring billing records
              </li>
            </ul>

            <h3 className="mt-6 text-2xl font-medium text-black">2.3 Recommended Usage</h3>

            <ul className="ml-6 list-disc">
              <li className="text-black light">
                Retrieve the customer before transactions or subscriptions
              </li>
              <li className="text-black light">Use returned paymentMethodId, cardId, or achId</li>
            </ul>

            <div className="callout">
              <p className="mb-0 light">
                <strong>Note:</strong> If a customer does not yet exist:
                <br />
                1. Create the customer using <b>Create Customer</b>
                <br />
                2. Retrieve the customer using the <b>Get Customer by ID</b>
                <br />
                3. Proceed with transaction or subscription creation
              </p>
            </div>

            {/* 3. ADDITIONAL */}
            <h2 className="text-black large">3. Additional Customer Vault Endpoints</h2>

            <h3 className="mt-4 text-2xl font-medium text-black">Address Management</h3>
            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/POST/api/gateway/{gatewayId}/customer/{customerId}/address"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Create
                </a>
                ,{' '}
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/PATCH/api/gateway/{gatewayId}/customer/{customerId}/address/{customerAddressId}"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  update
                </a>
                , or{' '}
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/DELETE/api/gateway/{gatewayId}/customer/{customerId}/address/{customerAddressId}"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  delete
                </a>{' '}
                customer addresses
              </li>
            </ul>

            <h3 className="mt-4 text-2xl font-medium text-black">Payment Method Management</h3>
            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/POST/api/gateway/{gatewayId}/customer/{customerId}/payment"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Create
                </a>
                ,{' '}
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/PATCH/api/gateway/{gatewayId}/customer/{customerId}/payment/{customerPaymentId}"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  update
                </a>
                , or{' '}
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/DELETE/api/gateway/{gatewayId}/customer/{customerId}/payment/{customerPaymentId}"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  delete
                </a>{' '}
                payment methods
              </li>
              <li className="text-black light">Limit: 25 methods per customer</li>
            </ul>

            <h3 className="mt-4 text-2xl font-medium text-black">Customer Search</h3>
            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/POST/api/gateway/{gatewayId}/customer/search"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Search
                </a>{' '}
                customer records using defined criteria
              </li>
            </ul>

            <div className="callout">
              <p className="mb-0 light">
                <strong>Note:</strong> GlobalSearch takes precedence. All other filters are applied
                as an <b>AND</b> condition.
              </p>
            </div>

            <h3 className="mt-4 text-2xl font-medium text-black">Payable Token</h3>
            <ul className="ml-6 list-disc">
              <li className="text-black light">
                <a
                  href="https://api.basyspro.com/iqsaas/v1/scalar/#tag/customer/POST/api/gateway/{gatewayId}/customer/{customerId}/payabletoken"
                  target="_blank"
                  rel="noreferrer"
                  className="links"
                >
                  Generate a token
                </a>{' '}
                linking a customer to outstanding invoices for payment workflows
              </li>
            </ul>

            {/* FLOW */}
            <h2 className="text-black large">4. Recommended End-to-End Flow</h2>

            <ol className="ml-6 list-decimal">
              <li className="text-black light">
                Tokenize payment data (card via iFrame or ACH via API)
              </li>
              <li className="text-black light">
                Create customer using <b>Create Customer API</b>
              </li>
              <li className="text-black light">Store returned customerId</li>
              <li className="text-black light">
                Retrieve customer using <b>Get Customer by ID</b> when needed
              </li>
              <li className="text-black light">
                Use stored methods for transactions, subscriptions, or invoices
              </li>
            </ol>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default DevCustomerVault;
